import { jsPDF } from "jspdf";
import { Context } from "svgcanvas";
import { canv } from "@/canv/global";
import { sleep } from "@/canv/utils";
import type { Group } from "@/group/group";
import "svg2pdf.js";
import { getViewport, sortByIndexByParent } from "@/canvasController/helpers";
import { selectionManager } from "@/tools/cursorTool";
import {
    addPdfPage,
    createPdfDoc,
    getDocSize,
    mapGroupsWithoutFrameToFrames,
    prepareAllImagesForExport,
    queryAreaForExport,
    savePDF,
} from "../utils";
import type { EmojiCache } from "./emojiExportUtils";
import { clearOutlineFontCache, convertTextToOutline, embedFontsForPdf } from "./fontUtils";
import { prepareSvgForExport } from "./utils";

export class VectorExport {
    async exportPage(groupsWithFrame: Group[], groupsWithoutFrame: Group[]) {
        const exportState = this.captureExportState();
        canv.setIsHighPdfExporting(true);

        try {
            // Получаем фигуры на фреймах
            const groupsByFrames = mapGroupsWithoutFrameToFrames(
                groupsWithFrame,
                groupsWithoutFrame,
            );
            const groupsForExport: Group[] = Object.values(groupsByFrames).reduce(
                (acc, cur) => {
                    acc.push(...cur);
                    return acc;
                },
                [...groupsWithFrame],
            );
            sortByIndexByParent(groupsForExport);
            await this.prepareImagesWithYield(groupsForExport);

            selectionManager.clearSelected();

            canv.zoomToSelected(groupsWithFrame, true);
            const areaWithFrames = canv.canvasController.getSelectedProps(groupsWithFrame);

            const visualWidth: number = areaWithFrames.width;
            const visualHeight: number = areaWithFrames.height;

            const svgCanvas = new Context({
                height: visualHeight,
                width: visualWidth,
                ctx: exportState.appCtx,
                enableMirroring: false,
            });

            canv.ctx = svgCanvas;
            canv.ctx.setTransform(1, 0, 0, 1, -areaWithFrames.startX, -areaWithFrames.startY);

            await this.renderCanvasWithYield(groupsForExport);

            const svgString = svgCanvas.getSerializedSvg();
            const parser = new DOMParser();
            const svgElement = parser.parseFromString(svgString, "image/svg+xml").documentElement;

            svgElement.setAttribute("viewBox", `0 0 ${visualWidth} ${visualHeight}`);
            svgElement.setAttribute("width", `${visualWidth}px`);
            svgElement.setAttribute("height", `${visualHeight}px`);

            await this.prepareSvgWithYield(svgElement);

            const doc = new jsPDF();
            await this.drawToDoc(svgElement, visualWidth, visualHeight, doc);
            savePDF(doc);
        } finally {
            this.restoreExportState(exportState);
        }
    }

    async exportPages(groupsWithFrame: Group[], groupsWithoutFrame: Group[]) {
        const exportState = this.captureExportState();
        canv.setIsHighPdfExporting(true);

        // Создаем кэш для эмодзи для переиспользования между страницами
        const emojiCache: EmojiCache = new Map();

        try {
            selectionManager.clearSelected();

            const groupsByFrames = mapGroupsWithoutFrameToFrames(
                groupsWithFrame,
                groupsWithoutFrame,
            );

            const firstFrame = groupsWithFrame[0];
            const doc = createPdfDoc(firstFrame.width, firstFrame.height);
            let isFirstPage = true;

            for (const frameGroup of groupsWithFrame) {
                const groupsOnFrame = groupsByFrames[frameGroup.UID] || [];
                const groupsForExport = [frameGroup].concat(groupsOnFrame);
                sortByIndexByParent(groupsForExport);
                await this.prepareImagesWithYield(groupsForExport);

                canv.zoomToSelected([frameGroup], true);

                const svgCanvas = new Context({
                    height: frameGroup.height,
                    width: frameGroup.width,
                    ctx: exportState.appCtx,
                    enableMirroring: false,
                });

                canv.ctx = svgCanvas;
                canv.ctx.setTransform(1, 0, 0, 1, -frameGroup.startX, -frameGroup.startY);

                await this.renderCanvasWithYield(groupsForExport);

                const svgString = svgCanvas.getSerializedSvg();
                const svgElement = new DOMParser().parseFromString(
                    svgString,
                    "image/svg+xml",
                ).documentElement;

                svgElement.setAttribute("viewBox", `0 0 ${frameGroup.width} ${frameGroup.height}`);
                svgElement.setAttribute("width", `${frameGroup.width}px`);
                svgElement.setAttribute("height", `${frameGroup.height}px`);
                await this.prepareSvgWithYield(svgElement, emojiCache);

                await this.drawToDoc(
                    svgElement,
                    frameGroup.width,
                    frameGroup.height,
                    doc,
                    isFirstPage,
                );

                isFirstPage = false;
            }

            savePDF(doc);
        } finally {
            emojiCache.clear();
            this.restoreExportState(exportState);
        }
    }

    async exportViewport() {
        const exportState = this.captureExportState();
        canv.setIsHighPdfExporting(true);

        try {
            const viewport = getViewport();

            const groupsForExport =
                queryAreaForExport({
                    startX: viewport.viewportStartX,
                    startY: viewport.viewportStartY,
                    width: viewport.viewportWidth,
                    height: viewport.viewportHeight,
                }) ?? [];
            sortByIndexByParent(groupsForExport);
            await this.prepareImagesWithYield(groupsForExport);

            selectionManager.clearSelected();

            const visualWidth: number = viewport.viewportWidth;
            const visualHeight: number = viewport.viewportHeight;

            const svgCanvas = new Context({
                height: visualHeight,
                width: visualWidth,
                ctx: exportState.appCtx,
                enableMirroring: false,
            });

            canv.ctx = svgCanvas;
            canv.ctx.setTransform(1, 0, 0, 1, -viewport.viewportStartX, -viewport.viewportStartY);

            await this.renderCanvasWithYield(groupsForExport);

            const svgString = svgCanvas.getSerializedSvg();
            const parser = new DOMParser();
            const svgElement = parser.parseFromString(svgString, "image/svg+xml").documentElement;

            svgElement.setAttribute("viewBox", `0 0 ${visualWidth} ${visualHeight}`);
            svgElement.setAttribute("width", `${visualWidth}px`);
            svgElement.setAttribute("height", `${visualHeight}px`);

            await this.prepareSvgWithYield(svgElement);

            const doc = new jsPDF();
            await this.drawToDoc(svgElement, visualWidth, visualHeight, doc);
            savePDF(doc);
        } finally {
            this.restoreExportState(exportState);
        }
    }

    private async drawToDoc(
        svgElement: HTMLElement,
        visualWidth: number,
        visualHeight: number,
        doc: jsPDF,
        isFirstPage = true,
    ) {
        if (!isFirstPage) {
            addPdfPage(doc, visualWidth, visualHeight);
        }

        const docSize = getDocSize(doc, visualWidth, visualHeight);

        const embeddedFonts = await embedFontsForPdf(doc, svgElement);
        await convertTextToOutline(svgElement, embeddedFonts);

        await doc.svg(svgElement, docSize);
        await this.yieldToEventLoop();
    }

    /**
     * Освобождает event loop для обработки ping и других событий
     */
    private async yieldToEventLoop(): Promise<void> {
        await sleep(0);
    }

    /**
     * Подготавливает изображения для экспорта и освобождает event loop
     */
    private async prepareImagesWithYield(groups: Group[]): Promise<void> {
        await prepareAllImagesForExport(groups);
        await this.yieldToEventLoop();
    }

    /**
     * Рендерит canvas и освобождает event loop
     */
    private async renderCanvasWithYield(groupsForExport: Group[]): Promise<void> {
        canv.redraw();
        canv.redraw(true, true, groupsForExport);
        await this.yieldToEventLoop();
    }

    /**
     * Подготавливает SVG для экспорта и освобождает event loop
     */
    private async prepareSvgWithYield(
        svgElement: HTMLElement,
        emojiCache?: EmojiCache,
    ): Promise<void> {
        await prepareSvgForExport(svgElement, emojiCache);
        await this.yieldToEventLoop();
    }

    private captureExportState() {
        return {
            appCtx: canv.ctx,
            selectedGroups: canv.canvasController.getSelected(),
            transformation: {
                "0": canv.m[0],
                "3": canv.m[3],
                "4": canv.m[4],
                "5": canv.m[5],
            },
        };
    }

    private restoreExportState(exportState: ReturnType<VectorExport["captureExportState"]>) {
        clearOutlineFontCache();
        canv.ctx = exportState.appCtx;
        selectionManager.setSelected(exportState.selectedGroups);

        canv.m[0] = exportState.transformation["0"];
        canv.m[3] = exportState.transformation["3"];
        canv.m[4] = exportState.transformation["4"];
        canv.m[5] = exportState.transformation["5"];
        canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
        canv.setIsHighPdfExporting(false);
    }
}
